import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { NewReportSlideFragmentLayout } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';

import joinClasses from 'webapp/helpers/join-classes';
import ReportsFragmentGridAddFragmentButtonComponent from 'webapp/components/reports/fragment-grid/add-fragment-button';

interface ReportsFragmentGridAddRowComponentInterface {
	Args: {
		addRow: (fragmentType: NewReportSlideFragmentLayout, index: number) => void;
		index?: number;
		insertBefore?: boolean; // currently unused
		isVisible?: boolean;
		isPageless?: boolean;
		backgroundColor?: string | null;
		showAddRowPlaceholder?: boolean;
	};
}

export default class ReportsFragmentGridAddRowComponent extends Component<ReportsFragmentGridAddRowComponentInterface> {
	@service('request-queue') declare requestQueueService: Service.RequestQueue;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('flow/tour/reports-pageless')
	declare flowTourReportsPagelessService: Service.Flow.Tour.ReportsPageless;

	isClickDisabled = false;

	availableTourId = TourId.REPORTS__PAGELESS_ADD_BLOCKS;

	constructor(owner: Owner, args: ReportsFragmentGridAddRowComponentInterface['Args']) {
		super(owner, args);

		schedule('afterRender', () => {
			this.setupTourBindings();
		});
	}

	get hideAddActions() {
		return this.requestQueueService.isRequestInProgress && !this.args.isVisible;
	}

	@action
	addFragment(fragmentType: NewReportSlideFragmentLayout) {
		if (this.isClickDisabled) {
			return;
		}

		this.isClickDisabled = true;

		let row = 1;
		const index = this.args.index;
		if (index) {
			if (this.args.insertBefore) {
				row = index - 1 || 1;
			} else {
				row = index + 1;
			}
		}

		this.args.addRow(fragmentType, row);

		// re-enable after a short delay to prevent double clicks
		setTimeout(() => {
			this.isClickDisabled = false;
		}, 300);
	}

	private setupTourBindings() {
		this.flowTourReportsPagelessService.bindTourCheck(this.availableTourId, this, () => {
			if (this.checkTourStartConditions()) {
				this.flowTourReportsPagelessService.startTour(this.availableTourId);
			}
		});
	}

	private checkTourStartConditions(): boolean {
		return this.args.isVisible !== false && this.args.isPageless === true;
	}

	override willDestroy() {
		super.willDestroy();
		this.flowTourReportsPagelessService.unbindTourCheck(this.availableTourId, this);
	}

	<template>
		<div
			class={{joinClasses (array "add-fragment" "add-fragment--row")}}
			{{! dragging this container caused random fragment additions and reordering }}
			draggable="false"
		>
			<div
				class="add-fragment-wrapper
					{{if this.hideAddActions 'add-fragment-wrapper--hidden'}}"
			>
				<div class="add-fragment__actions">
					<ReportsFragmentGridAddFragmentButtonComponent
						@isVisible={{@isVisible}}
						@onAddFragment={{this.addFragment}}
						@canAddDataboardSlide={{@isPageless}}
						@backgroundColor={{@backgroundColor}}
					/>
					{{! <Common::UiIcon @name="GripVertical" @size="sm" @class="handle-vertical" /> }}
				</div>
				<div
					class="add-fragment__text
						{{if @showAddRowPlaceholder 'add-fragment__text--always-visible'}}"
				>
					<span
						role="button"
						{{on "click" (fn this.addFragment NewReportSlideFragmentLayout.TEXT)}}
					>
						Write something, or click "+" for additional options
					</span>
				</div>
			</div>
		</div>
	</template>
}
